import { useCallback, useEffect, useRef, useState, type ReactNode } from "react"
import Image from "next/image"
import { LexicalText, Meeting } from "@/schema"
import { NoteTemplate } from "@/schema/note-template"
import { getSessionByMeetingId, RecordingSession } from "@/services/recording-db"
import { discardStoredRecording, resumeUpload } from "@/services/recording-resilient-uploader"
import { useMeetingRecordingStore } from "@/stores/use-meeting-recording-store"
import { useQueryClient } from "@tanstack/react-query"
import { ReadonlyAttribute } from "@ui/forms/inline"
import { Button, ScrollArea, Skeleton } from "@ui/shad"
import { cn } from "@ui/utils"
import { isPast } from "date-fns"
import {
  AlertCircle,
  Calendar,
  Check,
  ContactRound,
  Copy,
  Link,
  Link2,
  Loader2,
  StickyNote,
  Tag,
  Tags,
  Upload,
  X,
} from "lucide-react"
import { useCopyToClipboard } from "react-use"
import { toast } from "sonner"

import { isAllBooksId } from "@/lib/all-books"
import { canRescheduleEvents } from "@/lib/feature-flags"
import { liveCaptureAllowed } from "@/lib/live-capture"
import { isMeetingPromptInProgress, meetingPromptStatus } from "@/lib/prompt-status"
import { useRenderNoteTemplate } from "@/hooks/api/company-note-templates"
import { useFetchFeatureFlags } from "@/hooks/api/feature-flags/queries"
import { useSavePrepAsNotes, useUpdateMeeting } from "@/hooks/api/meetings"
import { useApiContext } from "@/hooks/api/use-api-context"
import { useFetchCurrentUser, useFetchUsers } from "@/hooks/api/users/queries"
import { useBookId } from "@/hooks/use-book-id"
import { useDebounce } from "@/hooks/use-debounce"
import { usePartnerAccess } from "@/hooks/use-partner-access"
import { useStartRecording } from "@/hooks/use-start-recording"
import RescheduleButton from "@/components/calendar/reschedule-button"
import { ConfirmDialog } from "@/components/dialogs"
import { Editor, EditorRefHandle, MarkdownReadonly } from "@/components/lexical"
import { MeetingAuditLogLink } from "@/components/meetings/meeting-audit-log-link"
import { MeetingUnknownParticipants } from "@/components/meetings/meeting-participant-add-menu"
import MeetingTopicSelector from "@/components/meetings/meeting-topic-selector"
import MeetingTypeSelector from "@/components/meetings/meeting-type-selector"
import { MeetingNotepadHistoryButton } from "@/components/meetings/meeting-version-dialog"
import { RecordMeetingDropdown } from "@/components/meetings/record-meeting-dropdown"
import { SummaryNotation } from "@/components/meetings/summary-notation"
import { UploadMeetingDropdown } from "@/components/meetings/upload-meeting-dropdown"
import { NoteTemplatePicker } from "@/components/notes/note-template-picker"
import { PartnerFeatureGate } from "@/components/partners/partner-feature-gate"
import { PromptButtons } from "@/components/prompts/button-container"

import AssociationsSelector from "./associations-selector"
import { BeneficiaryReviewCard } from "./beneficiary-review-card"
import { MeetingBookInfo } from "./meeting-book-info"
import MeetingDateTimeEditor from "./meeting-date-time-editor"

interface MeetingPrepViewProps {
  meeting?: Meeting
  isLoading: boolean
  skinny?: boolean
  startRecording?: boolean
  showBookInfo?: boolean
  commentsAction?: ReactNode
}

function PrepContent(props: React.ComponentProps<typeof PrepContentInner>) {
  return (
    <PartnerFeatureGate feature="notetaker">
      <PrepContentInner {...props} />
    </PartnerFeatureGate>
  )
}

function PrepContentInner({
  meeting,
  skinny,
  hasPrepContent,
  isPrepInProgress,
  hasPrepFailed,
}: {
  meeting: Meeting
  skinny?: boolean
  hasPrepContent: boolean
  isPrepInProgress: boolean
  hasPrepFailed: boolean
}) {
  const [, copyToClipboard] = useCopyToClipboard()
  const [copied, setCopied] = useState(false)
  const [showSaveDialog, setShowSaveDialog] = useState(false)
  const savePrepAsNotes = useSavePrepAsNotes()

  const hasHouseholds = (meeting.households?.length ?? 0) > 0

  const handleCopyMarkdown = () => {
    if (meeting.ai_preparation) {
      const cleaned = meeting.ai_preparation.replace(/^#{1,6}\s+/gm, "").replace(/\*\*/g, "")
      copyToClipboard(meeting.ai_notice ? `${cleaned}\n\n${meeting.ai_notice}` : cleaned)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }

  const handleSaveAsNote = () => {
    savePrepAsNotes.mutate(meeting.id, {
      onSuccess: (data) => {
        setShowSaveDialog(false)
        toast.success(`Meeting prep saved as ${data.notes_created === 1 ? "note" : "notes"}`)
      },
    })
  }

  return (
    <>
      <div
        className={cn(
          "border-sand-300 bg-sand-50 rounded-md border shadow-sm",
          !skinny && "hidden w-1/2 max-w-md lg:block 2xl:max-w-lg"
        )}
      >
        <div
          className="border-sand-300 bg-sand-200 text-sand-950 relative flex items-center gap-2 rounded-t-md border-b px-3 py-2"
          style={{ "--shimmer-color": "var(--color-sand-400)", "--shimmer-duration": "5s" } as React.CSSProperties}
        >
          <div className="animate-shimmer absolute inset-0 opacity-20" />
          <>
            <Image src="/sand_icon_900.png" alt="" width={14} height={14} className="size-3.5 flex-none dark:hidden" />
            <Image
              src="/sand_icon_300.png"
              alt=""
              width={14}
              height={14}
              className="hidden size-3.5 flex-none dark:block"
            />
          </>
          <h3 className="text-base/none font-medium">Meeting prep</h3>
          <div className="ml-auto flex items-center gap-2">
            <PromptButtons
              size="sm"
              promptType="Prompt::MeetingPrep"
              recordId={meeting.id}
              bookId={meeting.book_id}
              meetingType={meeting.meeting_type}
              isPromptRunInProgress={isPrepInProgress}
            />
            {hasPrepContent && hasHouseholds && (
              <Button
                onClick={() => setShowSaveDialog(true)}
                variant="ghost"
                size="iconSm"
                className="text-sand-600 hover:text-sand-900 hover:bg-sand-300"
                title="Save as note"
              >
                <StickyNote />
              </Button>
            )}
            {hasPrepContent && (
              <Button
                onClick={handleCopyMarkdown}
                variant="ghost"
                size="iconSm"
                className="text-sand-600 hover:text-sand-900 hover:bg-sand-300"
              >
                {copied ? <Check /> : <Copy />}
              </Button>
            )}
          </div>
        </div>
        {hasPrepContent && (
          <>
            <MarkdownReadonly markdown={meeting.ai_preparation || ""} className="text-sand-900 mt-3 p-3 pt-0" />
            <SummaryNotation aiNotice={meeting.ai_notice} className="mx-3 mb-3" />
          </>
        )}
        {!hasPrepContent && isPrepInProgress && (
          <div className="text-muted-foreground flex items-center gap-2 p-3 text-sm">
            <Loader2 className="size-4 animate-spin" />
            <span>Generating meeting prep...</span>
          </div>
        )}
        {!hasPrepContent && hasPrepFailed && (
          <div className="text-red-foreground p-3 text-sm">
            Meeting prep generation failed. You can retry from the prompt controls.
          </div>
        )}
      </div>
      <ConfirmDialog
        dismiss="explicit"
        title={"Save as note"}
        open={showSaveDialog}
        onOpenChange={setShowSaveDialog}
        description={
          <>
            This will save the meeting prep as a note on{" "}
            {meeting.households?.length === 1 ? meeting.households[0].name : `${meeting.households?.length} households`}
            .
          </>
        }
        action={{
          label: "Save as note",
          onClick: handleSaveAsNote,
          disabled: savePrepAsNotes.isPending,
          pending: savePrepAsNotes.isPending,
        }}
        cancel={{ onClick: () => setShowSaveDialog(false) }}
      ></ConfirmDialog>
    </>
  )
}

export default function MeetingPrepView({
  meeting,
  isLoading,
  skinny,
  startRecording: shouldAutoRecord,
  showBookInfo,
  commentsAction,
}: MeetingPrepViewProps) {
  const { enabled: fullProductAccess } = usePartnerAccess("full_product_access")
  const updateMeeting = useUpdateMeeting()
  const renderTemplate = useRenderNoteTemplate()
  const editorRef = useRef<EditorRefHandle>(null)
  const { data: currentUser } = useFetchCurrentUser()
  const { data: usersData } = useFetchUsers()
  const bookId = useBookId()
  const featureFlags = useFetchFeatureFlags()
  const meetingTopicsEnabled = featureFlags.data?.includes("meeting_topics") ?? false
  const shouldShowBookInfo = showBookInfo ?? isAllBooksId(bookId)
  const [, setCurrentNotepad] = useState<LexicalText | undefined>(meeting?.notepad)
  const { isVisible, isRecording, finishRecording } = useMeetingRecordingStore()
  const { start: startWebRecording, isStarting: isStartingRecording } = useStartRecording()
  const [incompleteSession, setIncompleteSession] = useState<RecordingSession | null>(null)
  const [isResuming, setIsResuming] = useState(false)
  const [showDiscardDialog, setShowDiscardDialog] = useState(false)
  const [isDiscarding, setIsDiscarding] = useState(false)
  const hasAutoRecordedRef = useRef(false)
  const getContext = useApiContext()
  const queryClient = useQueryClient()

  useEffect(() => {
    if (!meeting?.id) return

    const checkForIncompleteSession = async () => {
      const session = await getSessionByMeetingId(meeting.id)
      if (session && session.status !== "completed") {
        setIncompleteSession(session)
      }
    }

    checkForIncompleteSession()
  }, [meeting?.id])

  const debouncedUpdateMeeting = useDebounce((lexicalText: LexicalText) => {
    if (!meeting?.id) return
    updateMeeting.mutate({ meetingId: meeting.id, data: { notepad: lexicalText } })
  }, 1000)

  const handleNotepadUpdate = useCallback(
    (lexicalText: LexicalText) => {
      setCurrentNotepad(lexicalText)
      debouncedUpdateMeeting(lexicalText)
    },
    [debouncedUpdateMeeting]
  )

  const handleApplyTemplate = useCallback(
    (template: NoteTemplate) => {
      if (!template.content || !meeting) return

      const applyContent = (content: LexicalText) => {
        editorRef.current?.updateContent(content)
        debouncedUpdateMeeting(content)
      }

      renderTemplate.mutate(
        { noteTemplateId: template.id, meetingId: meeting.id },
        {
          onSuccess: (data) => {
            if (data.note_template_render.content) {
              applyContent(data.note_template_render.content)
            } else {
              applyContent(template.content!)
            }
          },
          onError: () => {
            applyContent(template.content!)
          },
        }
      )
    },
    [meeting, renderTemplate, debouncedUpdateMeeting]
  )

  const handleRecordingToggle = async () => {
    if (!meeting?.id) return
    if (isRecording) await finishRecording()
    else await startWebRecording({ meetingId: meeting.id, meetingTitle: meeting.title, getContext })
  }

  const handleResumeUpload = async () => {
    if (!fullProductAccess || !incompleteSession || !meeting?.id || !incompleteSession.sessionId) return

    setIsResuming(true)
    try {
      const result = await resumeUpload(
        meeting.id,
        incompleteSession.sessionId,
        incompleteSession.sessionGuid,
        () => getContext(),
        incompleteSession.mimeType,
        undefined,
        true
      )

      await queryClient.invalidateQueries({ queryKey: ["meeting"] })
      await queryClient.invalidateQueries({ queryKey: ["meetings"] })

      setIncompleteSession(null)
      if (result.outcome === "abandoned") toast.error("No saved audio was available to recover.")
      else toast.success("Recording upload completed")
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Failed to resume upload")
    } finally {
      setIsResuming(false)
    }
  }

  const handleDiscardIncomplete = async () => {
    if (!incompleteSession?.sessionId || !meeting?.id) return
    if (isResuming || isDiscarding) return
    setIsDiscarding(true)
    try {
      await discardStoredRecording(meeting.id, incompleteSession.sessionId, incompleteSession.sessionGuid, getContext)
      setIncompleteSession(null)
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Could not discard this recording")
    } finally {
      setIsDiscarding(false)
      setShowDiscardDialog(false)
    }
  }

  // Auto-start recording if requested via query param
  useEffect(() => {
    if (!meeting || isLoading || isRecording || hasAutoRecordedRef.current || !shouldAutoRecord) return

    // Use same logic as shouldShowRecordButton
    const end = meeting.end_time ? new Date(meeting.end_time) : undefined
    // For instant meetings without an end time, they're considered "active" not "past"
    const isPastMeeting = end ? isPast(end) : false
    const hasTranscript = Boolean(meeting.has_transcript)
    const canRecord =
      liveCaptureAllowed(meeting) &&
      !isPastMeeting &&
      !hasTranscript &&
      !meeting.is_processing_recording &&
      !meeting.has_recordings &&
      !meeting.notetaker?.id

    if (canRecord) {
      hasAutoRecordedRef.current = true
      handleRecordingToggle()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldAutoRecord, meeting, isRecording, isLoading])

  if (isLoading) {
    return (
      <div className="relative h-[calc(100vh-48px)] w-full">
        <div className="bg-background absolute inset-0 flex items-center justify-center">
          <Skeleton className="size-8 animate-spin" />
        </div>
      </div>
    )
  }

  if (!meeting) {
    return (
      <div className="flex h-[calc(100vh-48px)] w-full items-center justify-center">
        <span className="text-destructive">Failed to load meeting</span>
      </div>
    )
  }

  const unknownParticipants = meeting?.unknown_participants ?? []

  const title = meeting.title || "Untitled meeting"
  const hasPrepContent = Boolean(meeting.ai_preparation?.trim())
  const isPrepInProgress = isMeetingPromptInProgress(meeting, "Prompt::MeetingPrep")
  const hasPrepFailed = meetingPromptStatus(meeting, "Prompt::MeetingPrep") === "failed"
  const shouldShowPrepPanel = hasPrepContent || isPrepInProgress || hasPrepFailed

  const end = meeting.end_time ? new Date(meeting.end_time) : undefined
  const isPastMeeting = end ? isPast(end) : false // For instant meetings without an end time, they're considered "active" not "past"
  const hasTranscript = Boolean(meeting.has_transcript)
  const shouldShowRecordButton =
    !isPastMeeting &&
    !hasTranscript &&
    !meeting.is_processing_recording &&
    !meeting.has_recordings &&
    !meeting.notetaker?.id

  const isOngoing = isPast(new Date(meeting.start_time)) && !isPastMeeting
  const canReschedule = canRescheduleEvents(currentUser?.email) && !isPastMeeting && meeting.calendar_event_id

  return (
    <ScrollArea className="h-[calc(100vh-48px)]">
      <div
        className={cn(
          "mx-auto",
          skinny ? "w-full max-w-[28vw] px-4 pt-6" : "flex w-full justify-center gap-4 py-12 xl:gap-12 xl:px-8"
        )}
      >
        {!skinny && shouldShowPrepPanel && (
          <PrepContent
            meeting={meeting}
            skinny={skinny}
            hasPrepContent={hasPrepContent}
            isPrepInProgress={isPrepInProgress}
            hasPrepFailed={hasPrepFailed}
          />
        )}
        <div className={cn(skinny ? "w-full" : shouldShowPrepPanel ? "w-1/2" : "w-full", "max-w-screen-sm")}>
          {incompleteSession && !isRecording && (
            <PartnerFeatureGate feature="notetaker">
              <div className="bg-yellow-background border-yellow-border text-yellow-foreground mb-4 flex items-start gap-3 rounded-lg border p-3">
                <AlertCircle className="mt-0.5 size-4 shrink-0" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium">Incomplete recording found</p>
                  <p className="text-yellow-foreground/80 mt-1 text-xs">
                    {incompleteSession.totalChunks - incompleteSession.uploadedChunks} chunk(s) still need to be
                    uploaded.
                  </p>
                </div>
                <div className="flex shrink-0 gap-2">
                  <Button
                    size="xs"
                    variant="outline"
                    onClick={handleResumeUpload}
                    disabled={isResuming || isDiscarding}
                  >
                    {isResuming ? <Loader2 className="animate-spin" /> : <Upload />}
                    Resume
                  </Button>
                  <Button
                    size="iconXs"
                    variant="ghost"
                    onClick={() => setShowDiscardDialog(true)}
                    disabled={isResuming || isDiscarding}
                    title="Discard recording"
                  >
                    <X />
                  </Button>
                </div>
              </div>
            </PartnerFeatureGate>
          )}
          <div
            className={cn(
              "mt-2 flex items-center justify-between gap-2",
              commentsAction && "flex-wrap",
              skinny ? "text-xl font-semibold" : "text-2xl font-bold"
            )}
          >
            <span className={commentsAction ? "min-w-0 grow basis-48" : undefined}>{title}</span>
            {commentsAction ? (
              <div className="flex shrink-0 flex-col items-start gap-1">
                <MeetingAuditLogLink meeting={meeting} />
                {commentsAction}
              </div>
            ) : (
              <MeetingAuditLogLink meeting={meeting} />
            )}
          </div>
          {(isOngoing || (shouldShowRecordButton && !isVisible)) && (
            <div className="mt-2 flex items-center gap-2">
              {isOngoing && <UploadMeetingDropdown meetingId={meeting.id} bookId={meeting.book_id} />}
              {shouldShowRecordButton && !isVisible && (
                <RecordMeetingDropdown
                  meeting={meeting}
                  onRecordOnWeb={handleRecordingToggle}
                  disabled={isStartingRecording}
                  recordLabel={isStartingRecording ? "Starting..." : "Record"}
                />
              )}
            </div>
          )}

          <div className="mt-4 space-y-2">
            <ReadonlyAttribute
              label="Date & time"
              placeholder="No date configured"
              LabelIcon={Calendar}
              hideLabel
              renderValue={() => (
                <div className="flex items-center gap-2">
                  <MeetingDateTimeEditor meeting={meeting} />
                  {canReschedule && <RescheduleButton meeting={meeting} />}
                </div>
              )}
            />
            <ReadonlyAttribute
              label="Meeting type"
              LabelIcon={Tag}
              hideLabel
              renderValue={() => <MeetingTypeSelector meeting={meeting} size="sm" />}
            />
            {meetingTopicsEnabled && (
              <ReadonlyAttribute
                label="Meeting topics"
                LabelIcon={Tags}
                hideLabel
                renderValue={() => <MeetingTopicSelector meeting={meeting} />}
              />
            )}
            {shouldShowBookInfo && <MeetingBookInfo meeting={meeting} users={usersData?.users} />}
            <ReadonlyAttribute
              label="Conference link"
              value={meeting.meeting_link}
              placeholder="No link configured"
              LabelIcon={Link}
              href={meeting.meeting_link}
              hideLabel
            />
            <ReadonlyAttribute
              label="Associations"
              hideLabel
              renderValue={() => <AssociationsSelector meeting={meeting} />}
              placeholder="No associations"
              LabelIcon={Link2}
            />
            <ReadonlyAttribute
              label="Other attendees"
              hideLabel
              renderValue={() => (
                <MeetingUnknownParticipants
                  participants={unknownParticipants}
                  emptyLabel="No other attendees"
                  meetingId={meeting.id}
                />
              )}
              placeholder="No other attendees"
              LabelIcon={ContactRound}
            />
          </div>

          <BeneficiaryReviewCard meeting={meeting} />

          <div className={cn("bg-background relative mt-4 pt-2", !skinny && "border-t")}>
            {skinny && shouldShowPrepPanel && (
              <PrepContent
                meeting={meeting}
                skinny={skinny}
                hasPrepContent={hasPrepContent}
                isPrepInProgress={isPrepInProgress}
                hasPrepFailed={hasPrepFailed}
              />
            )}
            {(!skinny || !shouldShowPrepPanel || !fullProductAccess) && (
              <div className="relative">
                <div className="absolute top-0 right-0 z-10 flex gap-2">
                  <MeetingNotepadHistoryButton meetingId={meeting.id} />
                  <NoteTemplatePicker onApply={handleApplyTemplate} />
                </div>
                <Editor
                  key={meeting.id}
                  ref={editorRef}
                  initialContent={meeting.notepad}
                  editable={true}
                  onChange={handleNotepadUpdate}
                  placeholder="Start typing or press '/' for commands"
                  startingPlaceholder="Add manual preparation notes here..."
                />
              </div>
            )}
          </div>
        </div>
      </div>
      <ConfirmDialog
        open={showDiscardDialog}
        onOpenChange={(open) => !isDiscarding && setShowDiscardDialog(open)}
        title="Discard recording?"
        description="This will delete the saved audio. This cannot be undone."
        cancel={{ disabled: isDiscarding }}
        action={{
          label: "Discard recording",
          onClick: handleDiscardIncomplete,
          destructive: true,
          pending: isDiscarding,
          disabled: isResuming,
        }}
      />
    </ScrollArea>
  )
}
